"use client";

import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Spinner } from "src/components/animation/Transition";
import {
  authFieldErrorClasses,
  authFieldLabelClasses,
  authFormAlertClasses,
  authPrimaryButtonClasses,
  authSecondaryButtonClasses,
  authSuccessTextLinkClasses,
  authTextLinkClasses,
  getAuthInputClasses,
} from "src/app/auth/authFormStyles";
import { Button } from "src/components/ui/Button";
import { zodResolverCompat } from "src/lib/validation/zodResolverCompat";

const validationSchema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email address"),
});

type FormValues = z.infer<typeof validationSchema>;

const genericResetError = "Failed to reset password. Please contact support.";

function getResetErrorMessage(payload: unknown, status: number) {
  if (status >= 500) {
    return genericResetError;
  }

  if (!payload || typeof payload !== "object") {
    return genericResetError;
  }

  if ("detail" in payload && typeof payload.detail === "string" && payload.detail.length > 0) {
    return payload.detail;
  }

  if ("email" in payload && Array.isArray(payload.email) && typeof payload.email[0] === "string") {
    return payload.email[0];
  }

  for (const value of Object.values(payload)) {
    if (Array.isArray(value) && typeof value[0] === "string") {
      return value[0];
    }
  }

  return genericResetError;
}

export default function ResetPasswordRequestForm({ loginHref }: Readonly<{ loginHref: string }>) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [requestEmail, setRequestEmail] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolverCompat(validationSchema),
    defaultValues: { email: "" },
  });

  const emailErrorId = errors.email ? "reset-email-error" : undefined;
  const submitErrorId = submitError ? "reset-submit-error" : undefined;
  const emailField = register("email", {
    onChange: () => {
      if (submitError) {
        setSubmitError(null);
      }
    },
  });

  async function onSubmit({ email }: FormValues) {
    setSubmitError(null);

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/v1/reset_password/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
        referrerPolicy: "origin-when-cross-origin",
        mode: "cors",
        credentials: "include",
      });

      const payload = (await response.json().catch(() => null)) as unknown;

      if (!response.ok) {
        setSubmitError(getResetErrorMessage(payload, response.status));
        return;
      }

      setRequestEmail(email);
      reset({ email: "" });
    } catch {
      setSubmitError(genericResetError);
    }
  }

  function onInvalid() {
    setSubmitError(null);
  }

  if (requestEmail) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="space-y-5 rounded-xl border border-purple-20 bg-purple-5 p-5 text-center shadow-sm dark:border-purple-400/20 dark:bg-purple-900/15 dark:shadow-black/20"
      >
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-dark-100 dark:text-gray-100">
            Check your email
          </h2>
          <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">
            If an account exists for{" "}
            <span className="font-semibold text-dark-100 dark:text-white">{requestEmail}</span>, a
            password reset link will arrive shortly.
          </p>
        </div>

        <div className="space-y-3">
          <Button
            type="button"
            variant="secondary"
            fullWidth
            size="lg"
            className={authSecondaryButtonClasses}
            onClick={() => setRequestEmail(null)}
          >
            Send another link
          </Button>

          <Link href={loginHref} className={authSuccessTextLinkClasses}>
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form
      className="space-y-5"
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      noValidate
      aria-busy={isSubmitting}
      aria-describedby={submitErrorId}
    >
      <div className="space-y-2">
        <label htmlFor="email" className={authFieldLabelClasses}>
          Work email
        </label>
        <input
          id="email"
          type="email"
          {...emailField}
          autoComplete="email"
          inputMode="email"
          required
          placeholder="you@example.com"
          aria-invalid={Boolean(errors.email)}
          aria-describedby={emailErrorId}
          aria-errormessage={emailErrorId}
          className={getAuthInputClasses({
            hasError: Boolean(errors.email),
            className: "font-medium",
          })}
        />
        {errors.email && (
          <p id={emailErrorId} className={authFieldErrorClasses}>
            {errors.email.message}
          </p>
        )}
      </div>

      {submitError && (
        <div id={submitErrorId} role="alert" aria-live="assertive" className={authFormAlertClasses}>
          {submitError}
        </div>
      )}

      <div className="space-y-4 pt-2">
        <Button
          type="submit"
          disabled={isSubmitting}
          fullWidth
          size="lg"
          className={authPrimaryButtonClasses}
        >
          {isSubmitting && <Spinner className="h-4 w-4" />}
          {isSubmitting ? "Sending..." : "Send Reset Link"}
        </Button>

        <Link href={loginHref} className={authTextLinkClasses}>
          Back to sign in
        </Link>
      </div>
    </form>
  );
}
